import React, { FC, useEffect, useState, useMemo } from 'react';
import * as propz from 'propz';
import { SchoolEvent, SchoolEventIndividualScore } from 'models/event';
import { getRivalsByEvent } from 'helpers/rivals';
import {
  isDistanceOrTimeTournamentEvent,
  isIndividualTournamentAndTeamSport,
} from 'helpers/event';
import {
  convertPointsToStringWithoutDelimeter,
  getAbbreviationColor,
} from 'helpers/eventView';
import { SCORING } from 'consts/sport';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FlagIcon from '@mui/icons-material/Flag';
import {
  getNewRecordLabel,
  getClubGenderConvertedFromSportGender,
} from 'helpers/score';
import { Record as RecordModel } from 'models/record';

interface RowData {
  place: number;
  name: string;
  schoolName: string;
  teamName: string;
  rawResult: number;
  pointsAwarded: number;
  isNewRecord: boolean;
  abbreviationResult?: string;
  abbreviationColor?: string;
  userId?: string;   
  teamId?: string;   
}

interface Props {
  event: SchoolEvent;
  activeSchoolId: string;
  allRecords: RecordModel[];
  pageDelaySeconds?: number;
  pageIndex?: number;
  pageCount?: number;
}

const TVIndividualResultsTable: FC<Props> = ({
  event,
  activeSchoolId,
  allRecords = [],
  pageDelaySeconds = 6,
  pageIndex = 0,
  pageCount = 1,
}) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [priorRecordStr, setPriorRecordStr] = useState<string>('');

  const isTeamEvent =
    event.sport.players === 'TEAM' || isIndividualTournamentAndTeamSport(event);

  const invitedSchoolsArr: any[] = propz.get(event, ['invitedSchools'], []);
  const hasTeamsColumn = invitedSchoolsArr.some(obj => {
    const arr = propz.get(obj, ['schoolIds'], []);
    return Array.isArray(arr) && arr.length > 0;
  });

  useEffect(() => {
    const isFinished = event.status === 'FINISHED';
    const allRivals: any[] = getRivalsByEvent(activeSchoolId, event) || [];
    const rivals = allRivals.filter(
      r => r && r.school && typeof r.school.id === 'string'
    );

    if (isTeamEvent) {
      const schoolMap: Record<string, string> = {};
      if (event.inviterSchool) {
        schoolMap[event.inviterSchool.id] = event.inviterSchool.name;
      }
      (event.invitedSchools || []).forEach(sch => {
        schoolMap[sch.id] = sch.name;
      });

      const existingTeamIds = new Set<string>();
      rivals.forEach(r => {
        const tid = propz.get(r, ['team', 'id']);
        if (typeof tid === 'string') existingTeamIds.add(tid);
      });

      (event.teamsData || []).forEach((team: any) => {
        if (typeof team.id === 'string' && !existingTeamIds.has(team.id)) {
          rivals.push({
            team: { id: team.id, name: team.name, players: [] },
            school: { id: team.schoolId, name: schoolMap[team.schoolId] || '' },
            newRecord: false,
          });
        }
      });
    }

    const eventScoring = propz.get(event, ['sport', 'scoring'], SCORING.MORE_SCORES);

    const eventTimestamp = new Date(propz.get(event, ['startTime'], '')).getTime();
    const eventAges: number[] = propz.get(event, ['ages'], []);
    const eventMinAge = Math.min(...eventAges);
    const eventMaxAge = Math.max(...eventAges);

    const matchingRecords: RecordModel[] = (allRecords || []).filter(rec => {
      const recAges: number[] = propz.get(rec, ['ages'], []);
      const recMinAge = Math.min(...recAges);
      const recMaxAge = Math.max(...recAges);
      const sameAgeGroup = recMinAge === eventMinAge && recMaxAge === eventMaxAge;

      const recGenderRaw = propz.get(rec, ['gender'], '') as string;
      const recGenderConv = getClubGenderConvertedFromSportGender(recGenderRaw);
      const sameGender =
        recGenderConv === event.gender || recGenderRaw + '_ONLY' === event.gender;

      const sameSport =
        propz.get(rec, ['sportId'], '') === propz.get(event, ['sport', 'id'], '');

      const recDate = new Date(propz.get(rec, ['date'], '')).getTime();
      const earlierThanEvent = recDate < eventTimestamp;

      return sameAgeGroup && sameGender && sameSport && earlierThanEvent;
    });

    let priorRecord: RecordModel | undefined;
    if (matchingRecords.length > 0) {
      priorRecord = matchingRecords.reduce((closest, candidate) => {
        const candTime = new Date(propz.get(candidate, ['date'], '')).getTime();
        const closestTime = new Date(propz.get(closest, ['date'], '')).getTime();
        return Math.abs(eventTimestamp - candTime) < Math.abs(eventTimestamp - closestTime)
          ? candidate
          : closest;
      }, matchingRecords[0]);
    }

    if (priorRecord && priorRecord.record != null) {
      const raw = priorRecord.record;
      const numericValue = typeof raw === 'string' ? Number(raw) : (raw as number);
      const formatted = convertPointsToStringWithoutDelimeter(event, numericValue);
      setPriorRecordStr(formatted);
    } else {
      setPriorRecordStr('');
    }

    const teamRows: {
      rival: any;
      rawResult: number;
      pointsAwarded: number;
      abbreviationResult?: string;
      abbreviationColor?: string;
      scoredViaTeamScore: boolean;
      teamId?: string;
      schoolId?: string;
    }[] = [];

    const individualRows: Omit<RowData, 'isNewRecord'>[] = [];

    if (isTeamEvent) {
      rivals.forEach((rival: any) => {
        const schoolId = propz.get(rival, ['school', 'id'], '');
        const teamId = propz.get(rival, ['team', 'id']);

        let rawResult = 0;
        let pointsAwarded = 0;
        let abbreviationResult: string | undefined;
        let abbreviationColor: string | undefined = undefined;
        let scoredViaTeamScore = false;

        if (typeof teamId === 'string') {
          const teamEntry: any =
            (event.results.teamScore || []).find((ts: any) => ts.teamId === teamId) ?? null;
          if (teamEntry && teamEntry.richScore) {
            rawResult = propz.get(teamEntry, ['richScore', 'result'], 0);
            pointsAwarded = propz.get(teamEntry, ['richScore', 'points'], 0);
            const maybeAbbrev = propz.get(teamEntry, ['richScore', 'abbreviationResult']);
            if (typeof maybeAbbrev === 'string') {
              abbreviationResult = maybeAbbrev;
              abbreviationColor = getAbbreviationColor(event, maybeAbbrev);
            }
            scoredViaTeamScore = true;
          }
        }

        if (!scoredViaTeamScore) {
          const schoolEntry: any =
            (event.results.schoolScore || []).find((ss: any) => ss.schoolId === schoolId) ?? null;
          if (schoolEntry && schoolEntry.richScore) {
            rawResult = propz.get(schoolEntry, ['richScore', 'result'], 0);
            pointsAwarded = propz.get(schoolEntry, ['richScore', 'points'], 0);
            const maybeAbbrev = propz.get(schoolEntry, ['richScore', 'abbreviationResult']);
            if (typeof maybeAbbrev === 'string') {
              abbreviationResult = maybeAbbrev;
              abbreviationColor = getAbbreviationColor(event, maybeAbbrev);
            }
          }
        }

        teamRows.push({
          rival,
          rawResult,
          pointsAwarded,
          abbreviationResult,
          abbreviationColor,
          scoredViaTeamScore,
          teamId: typeof teamId === 'string' ? teamId : undefined,
          schoolId,
        });
      });

      let bestIndex: number | null = null;
      teamRows.forEach((tr, idx) => {
        if (tr.rawResult <= 0) return;
        if (bestIndex === null) {
          bestIndex = idx;
        } else {
          const cand = tr.rawResult;
          const bestVal = teamRows[bestIndex].rawResult;
          if (eventScoring === SCORING.LESS_TIME) {
            if (cand < bestVal) bestIndex = idx;
          } else {
            if (cand > bestVal) bestIndex = idx;
          }
        }
      });

      let isBestNewRecord = false;
      if (bestIndex !== null && priorRecord && teamRows[bestIndex].rawResult > 0) {
        const priorValue = Number(priorRecord.record);
        const thisValue = Number(teamRows[bestIndex].rawResult);

        if (eventScoring === SCORING.LESS_TIME) {
          if (thisValue < priorValue) {
            isBestNewRecord = true;
          } else {
            const bestTr = teamRows[bestIndex];
            let bestRich: any = {};

            if (bestTr.scoredViaTeamScore && bestTr.teamId) {
              const matchingTeamEntry = (event.results.teamScore || []).find(
                (ts: any) => ts.teamId === bestTr.teamId
              );
              bestRich = matchingTeamEntry ? matchingTeamEntry.richScore : {};
            } else if (bestTr.schoolId) {
              const matchingSchoolEntry = (event.results.schoolScore || []).find(
                (ss: any) => ss.schoolId === bestTr.schoolId
              );
              bestRich = matchingSchoolEntry ? matchingSchoolEntry.richScore : {};
            }

            isBestNewRecord = getNewRecordLabel(priorRecord, bestRich, event) === 'NR';
          }
        } else {
          if (thisValue > priorValue) {
            isBestNewRecord = true;
          } else {
            const bestTr = teamRows[bestIndex];
            let bestRich: any = {};

            if (bestTr.scoredViaTeamScore && bestTr.teamId) {
              const matchingTeamEntry = (event.results.teamScore || []).find(
                (ts: any) => ts.teamId === bestTr.teamId
              );
              bestRich = matchingTeamEntry ? matchingTeamEntry.richScore : {};
            } else if (bestTr.schoolId) {
              const matchingSchoolEntry = (event.results.schoolScore || []).find(
                (ss: any) => ss.schoolId === bestTr.schoolId
              );
              bestRich = matchingSchoolEntry ? matchingSchoolEntry.richScore : {};
            }

            isBestNewRecord = getNewRecordLabel(priorRecord, bestRich, event) === 'NR';
          }
        }
      }

      const mappedTeamRows: RowData[] = teamRows.map((tr, idx) => {
        const unionSchoolName = propz.get(tr.rival, ['school', 'name'], '');
        const teamNameFromRival = propz.get(tr.rival, ['team', 'name'], '');
        return {
          place: 0,
          name: '',
          schoolName: unionSchoolName,
          teamName: teamNameFromRival,
          rawResult: tr.rawResult,
          pointsAwarded: tr.pointsAwarded,
          isNewRecord: false,
          abbreviationResult: tr.abbreviationResult,
          abbreviationColor: tr.abbreviationColor,
          userId: undefined,
          teamId: tr.teamId,
        };
      });

      mappedTeamRows.sort((a, b) => {
        const aZero = a.rawResult === 0 && a.pointsAwarded === 0;
        const bZero = b.rawResult === 0 && b.pointsAwarded === 0;
        if (aZero && !bZero) return 1;
        if (!aZero && bZero) return -1;
        if (a.pointsAwarded !== b.pointsAwarded) {
          return b.pointsAwarded - a.pointsAwarded;
        }
        if (isDistanceOrTimeTournamentEvent(event)) {
          return a.rawResult - b.rawResult;
        } else {
          return b.rawResult - a.rawResult;
        }
      });

      const finalTeamRows: RowData[] = [];

      if (!isFinished) {
        mappedTeamRows.forEach((r, idx) => {
          finalTeamRows.push({
            ...r,
            place: idx + 1,
            isNewRecord: false,
          });
        });
      } else {
        let lastPts: number | null = null;
        let lastRaw: number | null = null;
        let lastPlace: number | null = null;
        let currentPlace = 1;
        mappedTeamRows.forEach((r, idx) => {
          const isTie =
            r.pointsAwarded === lastPts &&
            r.rawResult === lastRaw &&
            lastPlace !== null;
          const place = isTie ? lastPlace! : currentPlace;
          finalTeamRows.push({ ...r, place });
          if (!isTie) {
            lastPts = r.pointsAwarded;
            lastRaw = r.rawResult;
            lastPlace = place;
          }
          currentPlace++;
        });

        if (bestIndex !== null && isBestNewRecord) {
          const bestTeamId = teamRows[bestIndex].teamId;
          if (bestTeamId) {
            finalTeamRows.forEach(r => {
              if (r.teamId === bestTeamId) {
                r.isNewRecord = true;
              }
            });
          }
        }
      }
      setRows(finalTeamRows);
    } else {
      const individualsDataArr: any[] = propz.get(event, ['individualsData'], []);
      const allIndScores: SchoolEventIndividualScore[] = propz.get(
        event,
        ['results', 'individualScore'],
        []
      );

      rivals.forEach((rival: any) => {
        if (!Array.isArray(rival.players)) return;

        rival.players.forEach((player: any) => {
          const userId = propz.get(player, ['userId']);
          const matchingInd = individualsDataArr.find(
            (i: any) => propz.get(i, ['userId']) === userId
          );
          const schoolName = matchingInd
            ? propz.get(matchingInd, ['schoolName'], '')
            : '';

          let teamName = '';
          if (matchingInd && hasTeamsColumn) {
            const mySchoolId = propz.get(matchingInd, ['schoolId'], '');
            for (const unionObj of invitedSchoolsArr) {
              const arr: string[] = propz.get(unionObj, ['schoolIds'], []);
              if (Array.isArray(arr) && arr.includes(mySchoolId)) {
                teamName = propz.get(unionObj, ['name'], '');
                break;
              }
            }
          }

          const playerScoreObj = allIndScores.find(sc => sc.userId === userId);
          const rawResult = playerScoreObj
            ? propz.get(playerScoreObj, ['richScore', 'result'], 0)
            : 0;
          const pointsAwarded = playerScoreObj
            ? propz.get(playerScoreObj, ['richScore', 'points'], 0)
            : 0;

          const first = propz.get(player, ['firstName'], '').trim();
          const last = propz.get(player, ['lastName'], '').trim();
          const name = [first, last].filter(Boolean).join(' ');

          let abbreviationResult: string | undefined;
          let abbreviationColor: string | undefined = undefined;
          if (
            playerScoreObj &&
            typeof propz.get(playerScoreObj, ['richScore', 'abbreviationResult']) ===
              'string'
          ) {
            abbreviationResult = propz.get(
              playerScoreObj,
              ['richScore', 'abbreviationResult']
            );
            if (abbreviationResult) {
              abbreviationColor = getAbbreviationColor(event, abbreviationResult);
            }
          }

          individualRows.push({
            place: 0,
            name,
            schoolName,
            teamName,
            rawResult,
            pointsAwarded,
            abbreviationResult,
            abbreviationColor,
            userId,
            teamId: undefined,
          });
        });
      });

      individualRows.sort((a, b) => {
        const aZero = a.rawResult === 0 && a.pointsAwarded === 0;
        const bZero = b.rawResult === 0 && b.pointsAwarded === 0;
        if (aZero && !bZero) return 1;
        if (!aZero && bZero) return -1;
        if (a.pointsAwarded !== b.pointsAwarded) {
          return b.pointsAwarded - a.pointsAwarded;
        }
        if (isDistanceOrTimeTournamentEvent(event)) {
          return a.rawResult - b.rawResult;
        } else {
          return b.rawResult - a.rawResult;
        }
      });

      const finalIndRows: RowData[] = [];
        if (!isFinished) {
          individualRows.forEach((r, idx) => {
            finalIndRows.push({
              ...r,
              place: idx + 1,
              isNewRecord: false,
            });
          });
        } else {
        let lastPts: number | null = null;
        let lastRaw: number | null = null;
        let lastPlace: number | null = null;
        let currentPlace = 1;
        individualRows.forEach((r, idx) => {
          const isTie =
            r.pointsAwarded === lastPts &&
            r.rawResult === lastRaw &&
            lastPlace !== null;
          const place = isTie ? lastPlace! : currentPlace;
          finalIndRows.push({ ...r, place, isNewRecord: false });
          if (!isTie) {
            lastPts = r.pointsAwarded;
            lastRaw = r.rawResult;
            lastPlace = place;
          }
          currentPlace++;
        });

        let bestIndex: number | null = null;
        finalIndRows.forEach((row, idx) => {
          if (row.rawResult <= 0) return;
          if (bestIndex === null) {
            bestIndex = idx;
          } else {
            const cand = row.rawResult;
            const bestVal = finalIndRows[bestIndex].rawResult;
            if (eventScoring === SCORING.LESS_TIME) {
              if (cand < bestVal) bestIndex = idx;
            } else {
              if (cand > bestVal) bestIndex = idx;
            }
          }
        });

        let isBestNewRecord = false;
        if (
          bestIndex !== null &&
          priorRecord &&
          finalIndRows[bestIndex].rawResult > 0
        ) {
          const priorValue = Number(priorRecord.record);
          const thisValue = Number(finalIndRows[bestIndex].rawResult);
          if (eventScoring === SCORING.LESS_TIME) {
            if (thisValue < priorValue) {
              isBestNewRecord = true;
            } else {
              const bestUserId = finalIndRows[bestIndex].userId;
              const matchingScoreObj = allIndScores.find(
                sc => sc.userId === bestUserId
              );
              const bestRich = matchingScoreObj ? matchingScoreObj.richScore : {};
              isBestNewRecord =
                getNewRecordLabel(priorRecord, bestRich as any, event) === 'NR';
            }
          } else {
            if (thisValue > priorValue) {
              isBestNewRecord = true;
            } else {
              const bestUserId = finalIndRows[bestIndex].userId;
              const matchingScoreObj = allIndScores.find(
                sc => sc.userId === bestUserId
              );
              const bestRich = matchingScoreObj ? matchingScoreObj.richScore : {};
              isBestNewRecord =
                getNewRecordLabel(priorRecord, bestRich as any, event) === 'NR';
            }
          }
        }

        if (!priorRecord && bestIndex !== null) {
          const bestUserId = finalIndRows[bestIndex].userId;
          const matchingScoreObj = allIndScores.find(
            sc => sc.userId === bestUserId
          );
          if (matchingScoreObj) {
            isBestNewRecord = Boolean(propz.get(matchingScoreObj, ['isRecord'], false));
          }
        }

        if (bestIndex !== null && isBestNewRecord) {
          finalIndRows[bestIndex].isNewRecord = true;
        }
      }
      setRows(finalIndRows);
    }
  }, [event, activeSchoolId, allRecords, isTeamEvent]);

  const pageSize = 7;
  const totalPages = pageCount;
  const safePageIndex = pageIndex >= 0 && pageIndex < totalPages ? pageIndex : 0;
  const start = safePageIndex * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  return (
    <Box
      sx={{
        display: 'flex',           
        flexDirection: 'column',   
        justifyContent: 'center',  
        alignItems: 'center',      
        height: '100%',            
        p: 2,                      
      }}
    >
      <TableContainer
        sx={{
          width: '95%',
          maxWidth: 1200,
          backgroundColor: 'transparent',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0px 6px 20px rgba(0,0,0,0.2)',
        }}
      >
        <Table sx={{ 
          minWidth: 700,
          borderCollapse: 'separate',
          borderSpacing: '0 8px',    
          backgroundColor: 'transparent',
          }} 
          size="medium">
          <TableHead>
            <TableRow>
              {/* Place */}
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.8rem',     
                  color: '#222',
                  backgroundColor: '#e0e0e0',
                  py: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Place
              </TableCell>

              {!isTeamEvent && (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.7rem',
                    color: '#222',
                    backgroundColor: '#e0e0e0',
                    py: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Name
                </TableCell>
              )}

              {isTeamEvent ? (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.7rem',
                    color: '#222',
                    backgroundColor: '#e0e0e0',
                    py: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Team
                </TableCell>
              ) : hasTeamsColumn ? (
                <>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1.7rem',
                      color: '#222',
                      backgroundColor: '#e0e0e0',
                      py: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Team
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1.7rem',
                      color: '#222',
                      backgroundColor: '#e0e0e0',
                      py: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    School
                  </TableCell>
                </>
              ) : (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.7rem',
                    color: '#222',
                    backgroundColor: '#e0e0e0',
                    py: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  School
                </TableCell>
              )}

              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.7rem',
                  color: '#222',
                  backgroundColor: '#e0e0e0',
                  py: 1.5,
                  whiteSpace: 'nowrap',
                  width: 80,           
                  px: 1,               
                }}
              >
                Result
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.7rem',
                  color: '#222',
                  backgroundColor: '#e0e0e0',
                  py: 1.5,
                  whiteSpace: 'nowrap',
                  width: 60,          
                }}
              />

              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.7rem',
                  color: '#222',
                  backgroundColor: '#e0e0e0',
                  py: 1.5,
                  whiteSpace: 'nowrap',
                  width: 40,          
                }}
              >
                Points
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.8rem',
                  color: '#222',
                  backgroundColor: '#e0e0e0',
                  py: 1.5,
                  whiteSpace: 'nowrap',
                  width: 60,          
                }}
              >
                NR
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.map((row, idx) => {
              const formattedResult = convertPointsToStringWithoutDelimeter(
                event,
                row.rawResult
              );

              const isFinished = event.status === 'FINISHED'
              const showResult = isFinished || row.rawResult > 0

              let rowBg = idx % 2 === 0 ? 'rgba(248,248,248,1)' : 'white';
              if (row.place === 1 && showResult) rowBg = 'rgba(255, 243, 224, 1)'; 
              if (row.place === 2 && showResult) rowBg = 'rgba(237, 238, 242, 1)'; 
              if (row.place === 3 && showResult) rowBg = 'rgba(243, 236, 223, 1)'; 

              return (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.85)',  
                    borderRadius: '4px',                        
                    '& td': { borderBottom: 'none' },           
                  }}
                >
                    <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',   
                      color: '#111',
                      px: 2,
                      py: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.place}
                    {row.place === 1 && showResult && (
                      <EmojiEventsIcon
                        sx={{
                          ml: 1,
                          color: 'gold',
                          verticalAlign: 'middle',
                          fontSize: '1.6rem',
                        }}
                      />
                    )}
                    {row.place === 2 && showResult && (
                      <EmojiEventsIcon
                        sx={{
                          ml: 1,
                          color: 'silver',
                          verticalAlign: 'middle',
                          fontSize: '1.6rem',
                        }}
                      />
                    )}
                    {row.place === 3 && showResult && (
                      <EmojiEventsIcon
                        sx={{
                          ml: 1,
                          color: '#cd7f32',
                          verticalAlign: 'middle',
                          fontSize: '1.6rem',
                        }}
                      />
                    )}
                  </TableCell>

                  {!isTeamEvent && (
                    <TableCell
                      sx={{
                        fontSize: '1.6rem',
                        color: '#111',
                        px: 2,
                        py: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.name}
                    </TableCell>
                  )}

                  {isTeamEvent ? (
                    <TableCell
                      sx={{
                        fontSize: '1.6rem',
                        color: '#111',
                        px: 2,
                        py: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.schoolName}
                    </TableCell>
                  ) : hasTeamsColumn ? (
                    <>
                      <TableCell
                        sx={{
                          fontSize: '1.6rem',
                          color: '#111',
                          px: 2,
                          py: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.teamName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '1.6rem',
                          color: '#111',
                          px: 2,
                          py: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.schoolName}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell
                      sx={{
                        fontSize: '1.6rem',
                        color: '#111',
                        px: 2,
                        py: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.schoolName}
                    </TableCell>
                  )}

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',
                      color: '#111',
                     px: 2,
                      py: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                  {showResult ? formattedResult : ''}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',
                      color: row.abbreviationColor || '#111',
                      fontWeight: 'bold',
                      px: 2,
                      py: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.abbreviationResult || ''}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',
                      color: '#111',
                      px: 2,
                      py: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                  {showResult ? row.pointsAwarded : ''}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ width: '100px', py: 1.2 }}
                  >
                    {row.isNewRecord && (
                      <FlagIcon sx={{ color: 'red', fontSize: '1.6rem' }} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages>1 && (
        <Box sx={{mt:1,textAlign:'center'}}><Typography sx={{color:'#fff',fontSize:'1.4rem',fontWeight:'bold'}}>Page {safePageIndex+1} of {totalPages}</Typography></Box>
      )}
    </Box>
  );
};

export default TVIndividualResultsTable;